import { ARTEFACTS_AGENT_GATE } from '@omnea-app/packages/universal/artefacts/artefactsAgentGate'
import { recordAuthorization, tprmLevelsSwitch } from './accessRules'
import { all, always, any, ldFlag, omneanOnly, orgFlag } from './availability'
import {
  creates,
  hasEntityAuthorization,
  isSystemAdmin,
  managesConfigArea,
  managesInsights,
  managesPermissions,
  viewsDocuments,
  viewsInsights,
  viewsAllSubprocessors,
  viewsRenewals,
} from './fgaRules'
import { VIEW_SUPPLIER_RISK_MANAGEMENT_TPRM, VIEW_SUPPLIER_SUBPROCESSORS } from './recordRules'
import {
  adminsOnly,
  adminsOrAssigneeRoles,
  everyRole,
  everyRoleExceptSuppliers,
  nonViewerAdmins,
  superAdmin,
  superAdminOrP2pAdmin,
  type RoleAuthorizationRule,
} from './roleAuthorization'
import type { AuthorizationRule, RecordAccessRule, RecordGate, RecordVerb, TprmLevelsSwitch } from './accessRules'
import type { AvailabilityRule } from './availability'
import type { RenderedPermissionSetEntity } from './renderedPermissionSetEntities'
import type {
  AgreementDetailPageTab,
  BillDetailPageTab,
  RequestDetailPageTab,
  RequestRiskSection,
  SupplierDetailPageTab,
} from '@omnea-app/fe-routing/routes/detailTabs'
import type { RouteKey } from '@omnea-app/fe-routing/routes/routeKeys'
import type { ConfigArea, PermissionSetEntity } from '@omnea-digital/authz-model'

/**
 * Which query parameter distinguishes routes sharing one pattern, and the value that selects this
 * route.
 */
export type RouteSegment<QueryParam extends string = string> = { queryParam: QueryParam; value: string }

type RouteAddress = {
  /**
   * The Next.js route pattern, as `router.route` reports it. Hand-maintained strings the compiler
   * cannot check against the pages directory: a pattern matching no real page makes its route
   * unresolvable, and so ungated.
   */
  pattern: string
  segment?: RouteSegment
}

/**
 * What reaching a route requires, shared by the routes that require the same thing. No `record` arm:
 * `useRouteAccess` answers every route at once with no record in scope, so one declared here would
 * report `pending` forever. A route's record half is its `record` field, which the gate binds an id to.
 */
type RouteAuthorizationRule = Exclude<AuthorizationRule, { kind: 'record' }>

/** `always` is taken without an access answer, so a route redirected that way never reaches a denial. */
type RouteRedirect = { when: 'always' | 'denied'; to: RouteKey }

type RouteAccessRule = {
  /** Required where `AccessRule` leaves it optional: an omission would make a gated module unconditional. */
  availability: AvailabilityRule
  authorization: RouteAuthorizationRule
  redirect?: RouteRedirect
}

/**
 * A route that shows one record carries its id as `[id]`, or as the first segment of a `[...id]` catch-all,
 * which the route gate checks before the page.
 */
type RecordRoutePattern = `${string}/[id]` | `${string}/[id]/${string}` | `${string}/[...id]`

/** How a route is addressed as a URL, where its pattern does not already say. */
type RouteNavigation = { params?: undefined; href?: string } | { params: readonly [string, ...string[]]; href?: undefined }

export type RouteDefinition =
  | (RouteAddress & RouteAccessRule & RouteNavigation & { record?: undefined })
  | (RouteAddress &
      RouteAccessRule & {
        /** The page is only as reachable as its contents, so the verb is not always `canView`. */
        record: RecordGate
        pattern: RecordRoutePattern
        params: readonly [string, ...string[]]
        href?: undefined
      })

const SETTINGS_FALLBACK_ROUTE = 'settingsNotifications'

export const DEFAULT_DENIED_REDIRECT = 'dashboard'

const onDenial = (to: RouteKey): RouteRedirect => ({ when: 'denied', to })

const alwaysTo = (to: RouteKey): RouteRedirect => ({ when: 'always', to })

/**
 * Most settings pages share one page file and are told apart by their mode segment. The exceptions are
 * the ones with a page file of their own: Next routes a concrete path ahead of the optional catch-all,
 * so a mode segment for those would match nothing and leave the route unresolvable — hence ungated.
 */
const SETTINGS_PATTERN = '/settings/[[...mode]]'

const modeSegment = (value: string): RouteSegment<'mode'> => ({ queryParam: 'mode', value })

const requestTabSegment = (value: RequestDetailPageTab): RouteSegment<'tab'> => ({ queryParam: 'tab', value })

const requestRiskSectionSegment = (value: RequestRiskSection): RouteSegment<'section'> => ({ queryParam: 'section', value })

const supplierTabSegment = (value: SupplierDetailPageTab): RouteSegment<'tab'> => ({ queryParam: 'tab', value })

const agreementTabSegment = (value: AgreementDetailPageTab): RouteSegment<'tab'> => ({ queryParam: 'tab', value })

const billTabSegment = (value: BillDetailPageTab): RouteSegment<'tab'> => ({ queryParam: 'tab', value })

const permissionSetPageSegment = (value: RenderedPermissionSetEntity): RouteSegment<'entity'> => ({ queryParam: 'entity', value })

/**
 * What a settings route requires: the configuration area it belongs to, answered by the service where
 * it can and by the caller's role otherwise.
 *
 * TODO(Permissions): the service arm deliberately omits the role dimension, so a configuration-area
 * grant alone admits a non-admin. Their writes will still fail — the backend derives a caller's
 * permission role from session claims alone, so no grant lifts them to `admin`, and most settings
 * endpoints exclude the `user` role. Those endpoints MUST authorize on the service before enforcement is
 * rolled out beyond the pilot organisations.
 */
const configGated = (area: ConfigArea, role: RoleAuthorizationRule = superAdmin): RouteAuthorizationRule => ({
  kind: 'fgaOrRole',
  fga: managesConfigArea(area),
  role,
})

/**
 * The rollout is folded into the answer rather than named here: `canManagePermissions` reads false until
 * RELEASE_FGA_CONFIG names the caller, and the enforcement flag plays no part, so permissions can be
 * configured before the organisation enforces them. A denial redirects rather than reporting the page
 * non-existent.
 */
const PERMISSIONS_ROUTE = {
  availability: always(),
  authorization: { kind: 'fga', fga: managesPermissions },
  redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
} as const satisfies RouteAccessRule

const PERMISSION_SET_PAGE_ROUTE = {
  ...PERMISSIONS_ROUTE,
  pattern: '/settings/permissions/permission-sets/[entity]',
  params: ['entity'],
} as const

/**
 * Under FGA the section opens on `can_view_insights`: the admin tiers, or a custom role granted
 * insights access. This admits view-only admins, who must stay read-only once inside: every
 * dashboard write endpoint excludes `viewOnlyAdmin`, and the pages answer `canEdit` from
 * `isNonViewerAdmin` as well as the caller's dashboard role. Both are load-bearing — a write
 * affordance gated on the role alone offers a viewer an action the service refuses.
 */
const REPORTING_ROUTE = {
  availability: always(),
  authorization: { kind: 'fgaOrRole', fga: viewsInsights, role: adminsOnly },
  redirect: onDenial(DEFAULT_DENIED_REDIRECT),
} as const satisfies RouteAccessRule

/** Conversational intake ships to Omneans ahead of the organisations the chat assistant is sold to. */
const AI_ROUTE = {
  availability: any([orgFlag('AI_CHAT_ASSISTANT'), all([orgFlag('OMNEANS_ONLY_CONV_INTAKE'), omneanOnly()])]),
  authorization: { kind: 'open' },
  redirect: onDenial(DEFAULT_DENIED_REDIRECT),
} as const satisfies RouteAccessRule

const ARTEFACTS_AGENT_AVAILABILITY = all([orgFlag(ARTEFACTS_AGENT_GATE.orgFeatureFlag), ldFlag(ARTEFACTS_AGENT_GATE.releaseFlag)])

/**
 * Each of these is shared by a landing route and the detail pages beneath it, which must answer alike:
 * a nested page left on its own rule stays reachable under terms its parent has stopped allowing.
 */
const AI_BULK_EXTRACTION_ROUTE = {
  availability: ldFlag('RELEASE_AI_BULK_EXTRACTION'),
  authorization: configGated('organisation_settings'),
  redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
} as const satisfies RouteAccessRule

const DOCUMENT_STORAGE_ROUTE = {
  // The artefacts agent has its documents reviewed against playbooks edited here, so its release flag opens the page.
  availability: any([orgFlag('ENABLE_DOCUMENT_EXTRACTION'), ARTEFACTS_AGENT_AVAILABILITY]),
  authorization: configGated('document_review'),
  redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
} as const satisfies RouteAccessRule

const ENVIRONMENT_SYNC_ROUTE = {
  availability: ldFlag('RELEASE_CONFIG_SYNC'),
  authorization: configGated('organisation_settings'),
  redirect: onDenial(DEFAULT_DENIED_REDIRECT),
} as const satisfies RouteAccessRule

const INTEGRATIONS_ROUTE = {
  availability: always(),
  authorization: configGated('it'),
  redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
} as const satisfies RouteAccessRule

const PUBLIC_API_ROUTE = {
  availability: all([orgFlag('PUBLIC_API_UI'), orgFlag('PUBLIC_API')]),
  authorization: configGated('it'),
  redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
} as const satisfies RouteAccessRule

const INTEGRATION_BUILDER_ROUTE = {
  availability: ldFlag('RELEASE_INTEGRATION_BUILDER_V2'),
  authorization: configGated('it'),
  redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
} as const satisfies RouteAccessRule

/** Shared with the `/workflows` routes, which mount the same pages outside the settings tree. */
const WORKFLOW_EDITOR_ROUTE = {
  availability: always(),
  authorization: configGated('workflows_and_forms', superAdminOrP2pAdmin),
  redirect: onDenial(DEFAULT_DENIED_REDIRECT),
} as const satisfies RouteAccessRule

/**
 * Starting a request, drafts included: a draft only resumes into creating one. Shared with the
 * new-request and Drafts links, which must agree with the route they lead
 * to. Before enforcement every app role could start one, so the role arm keeps that.
 */
export const CREATE_REQUEST_ROUTE = {
  availability: always(),
  authorization: { kind: 'fgaOrRole', fga: creates('request'), role: everyRoleExceptSuppliers },
  redirect: onDenial(DEFAULT_DENIED_REDIRECT),
} as const satisfies RouteAccessRule

// Role arms keep pre-FGA behaviour: the suppliers list was super-admin only, the form picker open to every role.
export const CREATE_SUPPLIER_ROUTE = {
  availability: always(),
  authorization: { kind: 'fgaOrRole', fga: creates('supplier'), role: superAdmin },
} as const satisfies RouteAccessRule

export const CREATE_SUPPLIER_IN_FORM_ROUTE = {
  availability: always(),
  authorization: { kind: 'fgaOrRole', fga: creates('supplier'), role: everyRole },
} as const satisfies RouteAccessRule

/** Keyed by entity so a new permission-set family cannot ship with its list route ungated. */
const ENTITY_LIST_ROUTES = {
  request: {
    availability: always(),
    authorization: { kind: 'fgaOrRole', fga: hasEntityAuthorization('request'), role: everyRoleExceptSuppliers },
  },
  supplier: {
    availability: always(),
    authorization: { kind: 'fgaOrRole', fga: hasEntityAuthorization('supplier'), role: adminsOrAssigneeRoles },
  },
  agreement: {
    availability: orgFlag('CREATE_AGREEMENT'),
    authorization: { kind: 'fgaOrRole', fga: hasEntityAuthorization('agreement'), role: everyRole },
  },
  bill: {
    availability: orgFlag('INVOICE_PROCESSING'),
    authorization: { kind: 'fgaOrRole', fga: hasEntityAuthorization('bill'), role: adminsOnly },
  },
  workflow: {
    availability: always(),
    authorization: { kind: 'fgaOrRole', fga: hasEntityAuthorization('workflow'), role: everyRoleExceptSuppliers },
  },
} as const satisfies Readonly<Record<PermissionSetEntity, RouteAccessRule>>

const RECORD_LANDING_KEYS = {
  request: 'requestDetailOverview',
  supplier: 'supplierDetailOverview',
  agreement: 'agreementDetailOverview',
  bill: 'billDetailOverview',
  // A workflow has no tabs of its own to fall back to, so a denial goes straight to the terminal landing dashboard.
  workflow: 'dashboard',
} as const satisfies Readonly<Record<PermissionSetEntity, RouteKey>>

/** An entity whose records have a page of their own, which is what a denied sub-route can fall back to. */
type RecordLandingEntity = keyof typeof RECORD_LANDING_KEYS

type RecordGateSpec<Entity extends RecordLandingEntity> =
  | RecordVerb<Entity>
  | readonly [RecordAccessRule, RecordAccessRule, ...RecordAccessRule[]]

/** A gate named by one verb stays typed as one rule, so a list can tell at compile time it can batch the check. */
type RecordGateFor<Gate> = Gate extends string ? RecordAccessRule : RecordGate

/**
 * A detail route answers as its list does, then the route gate holds the page until the record's own
 * check allows it. Both halves take their role arm from the one rule, so the record cannot be reached
 * by a caller the list would have turned away — the permissions service answers `role` for a record it
 * does not hold, which an organisation already enforcing still has for anything unmigrated, so that arm
 * has to reproduce what gated the record before the service existed.
 */
const detailRouteFor = <
  Entity extends RecordLandingEntity,
  Gate extends RecordGateSpec<Entity> | TprmLevelsSwitch<RecordGateSpec<Entity>>,
  Pattern extends RecordRoutePattern,
  Params extends readonly [string, ...string[]],
>(
  entity: Entity,
  gate: Gate,
  pattern: Pattern,
  params: Params
) => {
  const listRoute = ENTITY_LIST_ROUTES[entity]
  const { role } = listRoute.authorization
  const withRole = (rule: RecordAccessRule): RecordAccessRule => ({ ...rule, authorization: { ...rule.authorization, role } })
  const gateFromSpec = (spec: RecordGateSpec<Entity>): Exclude<RecordGate, { kind: 'tprmLevelsSwitch' }> => {
    if (typeof spec === 'string') return { authorization: recordAuthorization(entity, spec, role) }
    const [first, second, ...rest] = spec
    return { anyOf: [withRole(first), withRole(second), ...rest.map(withRole)] }
  }
  const recordGate = (spec: RecordGateSpec<Entity> | TprmLevelsSwitch<RecordGateSpec<Entity>>): RecordGate =>
    typeof spec !== 'string' && 'kind' in spec
      ? { ...spec, flagOff: gateFromSpec(spec.flagOff), flagOn: gateFromSpec(spec.flagOn) }
      : gateFromSpec(spec)

  return {
    ...listRoute,
    // TypeScript cannot carry the string check on `gate` into the conditional type, so it is restated here.
    record: recordGate(gate) as RecordGateFor<Gate>,
    redirect: onDenial(RECORD_LANDING_KEYS[entity]),
    pattern,
    params,
  } as const
}

/** The landing itself cannot send a denied caller to itself, so it leaves the record behind. */
const RECORD_LANDING_DENIAL = { redirect: onDenial(DEFAULT_DENIED_REDIRECT) }

const REQUEST_DETAIL_TAB_ROUTE = detailRouteFor('request', 'canView', '/requests/detail/[id]/[tab]', ['id', 'tab'])
const REQUEST_TPRM_GATE = tprmLevelsSwitch<RecordGateSpec<'request'>>({ flagOff: 'canManageTprm', flagOn: 'canViewTprm' })
const REQUEST_TPRM_TAB_ROUTE = detailRouteFor('request', REQUEST_TPRM_GATE, '/requests/detail/[id]/[tab]', ['id', 'tab'])

const REQUEST_RISKS_AVAILABILITY = any([
  orgFlag('TPRM'),
  orgFlag('TPRM_CONTINUOUS_MONITORING'),
  orgFlag('SUPPLIER_SUBPROCESSORS'),
])

/** Each section's own availability must stay within the family's, or a section would widen the tab. */
const REQUEST_RISK_SECTION_ROUTE = {
  ...detailRouteFor('request', REQUEST_TPRM_GATE, '/requests/detail/[id]/risks/[section]', ['id', 'section']),
  availability: REQUEST_RISKS_AVAILABILITY,
}

const SUPPLIER_DETAIL_TAB_ROUTE = detailRouteFor('supplier', 'canView', '/suppliers/detail/[id]/[tab]', ['id', 'tab'])

const AGREEMENT_DETAIL_TAB_ROUTE = detailRouteFor('agreement', 'canView', '/agreements/detail/[id]/[tab]', ['id', 'tab'])

const BILL_DETAIL_TAB_ROUTE = detailRouteFor('bill', 'canView', '/bills/detail/[id]/[tab]', ['id', 'tab'])

export const ROUTE_REGISTRY = {
  dashboard: { pattern: '/', availability: always(), authorization: { kind: 'open' } },
  tasks: { pattern: '/tasks/[[...mode]]', availability: always(), authorization: { kind: 'open' } },
  startRequest: { ...CREATE_REQUEST_ROUTE, pattern: '/start' },
  startDrafts: { ...CREATE_REQUEST_ROUTE, pattern: '/start/drafts' },
  intake: {
    ...CREATE_REQUEST_ROUTE,
    record: { authorization: recordAuthorization('workflow', 'canCreateRequest', CREATE_REQUEST_ROUTE.authorization.role) },
    pattern: '/intake/[...id]',
    params: ['id'],
  },
  ai: { ...AI_ROUTE, pattern: '/ai' },
  aiChat: { ...AI_ROUTE, pattern: '/ai/[mode]', params: ['mode'] },
  requestsList: { ...ENTITY_LIST_ROUTES.request, pattern: '/requests/[[...mode]]' },
  requestDetail: { ...REQUEST_DETAIL_TAB_ROUTE, redirect: alwaysTo('requestDetailOverview') },
  requestDetailOverview: { ...REQUEST_DETAIL_TAB_ROUTE, ...RECORD_LANDING_DENIAL, segment: requestTabSegment('overview') },
  requestDetailMyTasks: { ...REQUEST_DETAIL_TAB_ROUTE, segment: requestTabSegment('my-tasks') },
  requestDetailIntakeForm: { ...REQUEST_DETAIL_TAB_ROUTE, segment: requestTabSegment('intake-form') },
  requestDetailSource: { ...REQUEST_DETAIL_TAB_ROUTE, segment: requestTabSegment('source') },
  requestDetailSubmissions: { ...REQUEST_DETAIL_TAB_ROUTE, segment: requestTabSegment('submissions') },
  requestDetailRenewalAssessment: { ...REQUEST_DETAIL_TAB_ROUTE, segment: requestTabSegment('renewal-assessment') },
  requestDetailDocuments: {
    ...detailRouteFor('request', 'canViewDocuments', '/requests/detail/[id]/[tab]', ['id', 'tab']),
    segment: requestTabSegment('documents'),
  },
  requestDetailRisks: {
    ...REQUEST_TPRM_TAB_ROUTE,
    segment: requestTabSegment('risks'),
    availability: REQUEST_RISKS_AVAILABILITY,
  },
  requestDetailRisksSection: REQUEST_RISK_SECTION_ROUTE,
  requestDetailRisksInherentRisk: {
    ...REQUEST_RISK_SECTION_ROUTE,
    segment: requestRiskSectionSegment('inherent-risk'),
    availability: orgFlag('TPRM'),
  },
  requestDetailRisksControls: {
    ...REQUEST_RISK_SECTION_ROUTE,
    segment: requestRiskSectionSegment('controls'),
    availability: all([orgFlag('TPRM'), ldFlag('RELEASE_RISK_CONTROLS_AGENT')]),
  },
  requestDetailRisksMonitoring: { ...REQUEST_RISK_SECTION_ROUTE, segment: requestRiskSectionSegment('monitoring') },
  requestDetailRisksSubprocessors: { ...REQUEST_RISK_SECTION_ROUTE, segment: requestRiskSectionSegment('subprocessors') },
  requestDetailRisksRiskRegister: { ...REQUEST_RISK_SECTION_ROUTE, segment: requestRiskSectionSegment('risk-register') },
  requestDetailArtefacts: {
    ...REQUEST_TPRM_TAB_ROUTE,
    segment: requestTabSegment('artefacts'),
    availability: ARTEFACTS_AGENT_AVAILABILITY,
  },
  requestDetailAuctions: {
    ...REQUEST_DETAIL_TAB_ROUTE,
    segment: requestTabSegment('auctions'),
    availability: orgFlag('AUCTIONS'),
  },
  requestDetailWorkflow: {
    ...REQUEST_DETAIL_TAB_ROUTE,
    segment: requestTabSegment('workflow'),
    authorization: configGated('workflows_and_forms'),
  },
  requestDetailLineItems: { ...REQUEST_DETAIL_TAB_ROUTE, segment: requestTabSegment('line-items') },
  requestAuditLog: detailRouteFor('request', 'canAdminister', '/requests/detail/[id]/audit-log', ['id']),
  requestMyTask: detailRouteFor('request', 'canView', '/requests/detail/[id]/my-tasks/[taskIdOrFormId]', [
    'id',
    'taskIdOrFormId',
  ]),
  requestSubmission: detailRouteFor('request', 'canView', '/requests/detail/[id]/submissions/[requestFormId]', [
    'id',
    'requestFormId',
  ]),
  requestSubmissionTask: detailRouteFor('request', 'canView', '/requests/detail/[id]/submissions/tasks/[taskId]', [
    'id',
    'taskId',
  ]),
  requestRenewalAssessment: detailRouteFor('request', 'canView', '/requests/detail/[id]/renewal-assessment/[requestFormId]', [
    'id',
    'requestFormId',
  ]),
  requestAuction: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/auctions/[sideNav]', ['id', 'sideNav']),
    availability: orgFlag('AUCTIONS'),
  },
  requestRfxIndex: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx', ['id']),
    availability: orgFlag('RFX'),
  },
  requestRfx: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/[sideNav]', ['id', 'sideNav']),
    availability: orgFlag('RFX'),
  },
  requestRfxOverview: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/overview', ['id']),
    availability: orgFlag('RFX'),
  },
  requestRfxSetup: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/setup/[page]', ['id', 'page']),
    availability: orgFlag('RFX'),
  },
  requestRfxEvaluation: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/evaluation/[page]', ['id', 'page']),
    availability: orgFlag('RFX'),
  },
  requestRfxCommunications: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/communications/[page]', ['id', 'page']),
    availability: orgFlag('RFX'),
  },
  requestRfxRelated: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/related-workflows', ['id']),
    availability: orgFlag('RFX'),
  },
  requestRfxDocuments: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/documents', ['id']),
    availability: orgFlag('RFX'),
  },
  requestRfxDocument: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/documents/[page]', ['id', 'page']),
    availability: orgFlag('RFX'),
  },
  requestRfxAiScoring: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/[sideNav]/ai-scoring', ['id', 'sideNav']),
    availability: orgFlag('RFX'),
  },
  requestRfxDeadlines: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/[sideNav]/deadlines', ['id', 'sideNav']),
    availability: orgFlag('RFX'),
  },
  requestRfxResultsBreakdown: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/[sideNav]/results-breakdown', ['id', 'sideNav']),
    availability: orgFlag('RFX'),
  },
  requestRfxResultsBreakdownFormTemplate: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/[sideNav]/results-breakdown/[requestFormTemplateId]', [
      'id',
      'sideNav',
      'requestFormTemplateId',
    ]),
    availability: orgFlag('RFX'),
  },
  requestRfxScoring: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/[sideNav]/scoring/[requestStepTaskId]', [
      'id',
      'sideNav',
      'requestStepTaskId',
    ]),
    availability: orgFlag('RFX'),
  },
  requestRfxFormTemplate: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/[sideNav]/[requestFormTemplateId]', [
      'id',
      'sideNav',
      'requestFormTemplateId',
    ]),
    availability: orgFlag('RFX'),
  },
  requestRfxFormTemplateSupplierForms: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/[sideNav]/[requestFormTemplateId]/supplier-forms', [
      'id',
      'sideNav',
      'requestFormTemplateId',
    ]),
    availability: orgFlag('RFX'),
  },
  requestRfxFormTemplatePricingAnalysis: {
    ...detailRouteFor('request', 'canView', '/requests/detail/[id]/rfx/[sideNav]/[requestFormTemplateId]/pricing-analysis', [
      'id',
      'sideNav',
      'requestFormTemplateId',
    ]),
    availability: all([orgFlag('RFX'), orgFlag('RFX_PRICING_ANALYSIS')]),
  },
  suppliersList: { ...ENTITY_LIST_ROUTES.supplier, pattern: '/suppliers/[[...mode]]' },
  supplierDetailIndex: {
    ...detailRouteFor('supplier', 'canView', '/suppliers/detail/[id]', ['id']),
    redirect: alwaysTo('supplierDetailOverview'),
  },
  supplierDetail: { ...SUPPLIER_DETAIL_TAB_ROUTE, redirect: alwaysTo('supplierDetailOverview') },
  supplierDetailOverview: { ...SUPPLIER_DETAIL_TAB_ROUTE, ...RECORD_LANDING_DENIAL, segment: supplierTabSegment('overview') },
  supplierDetailActivity: { ...SUPPLIER_DETAIL_TAB_ROUTE, segment: supplierTabSegment('activity') },
  supplierDetailDocuments: { ...SUPPLIER_DETAIL_TAB_ROUTE, segment: supplierTabSegment('documents') },
  supplierDetailRiskManagement: {
    ...detailRouteFor(
      'supplier',
      tprmLevelsSwitch<RecordGateSpec<'supplier'>>({
        flagOff: 'canEditTprm',
        flagOn: [VIEW_SUPPLIER_RISK_MANAGEMENT_TPRM, VIEW_SUPPLIER_SUBPROCESSORS.flagOn],
      }),
      '/suppliers/detail/[id]/[tab]',
      ['id', 'tab']
    ),
    segment: supplierTabSegment('risk-management'),
    // One flag per section the page can show, so the tab is reachable whenever any of them has content.
    availability: any([
      orgFlag('TPRM'),
      orgFlag('TPRM_CONTINUOUS_MONITORING'),
      orgFlag('ENABLE_ASSESSMENTS'),
      orgFlag('SUPPLIER_SUBPROCESSORS'),
    ]),
  },
  supplierDetailContacts: { ...SUPPLIER_DETAIL_TAB_ROUTE, segment: supplierTabSegment('contacts') },
  supplierDetailHierarchy: { ...SUPPLIER_DETAIL_TAB_ROUTE, segment: supplierTabSegment('hierarchy') },
  // The page is the submission, not the supplier, and the two are granted separately.
  supplierDetailSubmission: detailRouteFor(
    'supplier',
    'canViewRelatedSubmissions',
    '/suppliers/detail/[id]/submissions/[submissionId]',
    ['id', 'submissionId']
  ),
  agreementsList: { ...ENTITY_LIST_ROUTES.agreement, pattern: '/agreements/[[...mode]]' },
  agreementDetailIndex: {
    ...detailRouteFor('agreement', 'canView', '/agreements/detail/[id]', ['id']),
    redirect: alwaysTo('agreementDetailOverview'),
  },
  agreementDetail: { ...AGREEMENT_DETAIL_TAB_ROUTE, redirect: alwaysTo('agreementDetailOverview') },
  agreementDetailOverview: { ...AGREEMENT_DETAIL_TAB_ROUTE, ...RECORD_LANDING_DENIAL, segment: agreementTabSegment('overview') },
  agreementDetailSubmissions: { ...AGREEMENT_DETAIL_TAB_ROUTE, segment: agreementTabSegment('submissions') },
  agreementDetailDocuments: {
    ...detailRouteFor('agreement', 'canViewDocuments', '/agreements/detail/[id]/[tab]', ['id', 'tab']),
    segment: agreementTabSegment('documents'),
  },
  agreementDetailHierarchy: { ...AGREEMENT_DETAIL_TAB_ROUTE, segment: agreementTabSegment('hierarchy') },
  agreementDetailSubmission: {
    ...detailRouteFor('agreement', 'canView', '/agreements/detail/[id]/[tab]/[submissionId]', ['id', 'tab', 'submissionId']),
    segment: agreementTabSegment('submissions'),
  },
  // Only the submissions tab shows a submission, but the pattern accepts any tab, so the rest are
  // addresses that name no page — and an unsegmented sibling is what lets the gate redirect them.
  agreementDetailSubmissionUnknownTab: {
    ...detailRouteFor('agreement', 'canView', '/agreements/detail/[id]/[tab]/[submissionId]', ['id', 'tab', 'submissionId']),
    redirect: alwaysTo('agreementDetailOverview'),
  },
  agreementAuditLog: detailRouteFor('agreement', 'canAdminister', '/agreements/detail/[id]/audit-log', ['id']),
  assessmentsList: {
    pattern: '/assessments/[[...mode]]',
    availability: orgFlag('ENABLE_ASSESSMENTS'),
    authorization: { kind: 'role', role: adminsOrAssigneeRoles },
  },
  renewalsList: {
    pattern: '/renewals/[[...mode]]',
    availability: always(),
    authorization: { kind: 'fgaOrRole', fga: viewsRenewals, role: adminsOrAssigneeRoles },
  },
  documentsList: {
    pattern: '/documents/[[...mode]]',
    availability: always(),
    authorization: { kind: 'fgaOrRole', fga: viewsDocuments, role: adminsOrAssigneeRoles },
  },
  purchaseOrdersList: {
    pattern: '/purchase-orders/[[...tab]]',
    availability: ldFlag('RELEASE_PURCHASE_ORDER_UPDATES'),
    authorization: { kind: 'role', role: adminsOrAssigneeRoles },
  },
  scheduledRequestsList: {
    pattern: '/scheduled-requests/[[...mode]]',
    availability: ldFlag('RELEASE_SCHEDULED_REQUESTS'),
    authorization: { kind: 'role', role: nonViewerAdmins },
  },
  budgetsList: {
    pattern: '/budgets/[[...mode]]',
    availability: orgFlag('BUDGETS'),
    authorization: { kind: 'role', role: adminsOnly },
  },
  billsList: { ...ENTITY_LIST_ROUTES.bill, pattern: '/bills/[[...tab]]' },
  billDetailIndex: {
    ...detailRouteFor('bill', 'canView', '/bills/detail/[id]', ['id']),
    redirect: alwaysTo('billDetailOverview'),
  },
  billDetail: { ...BILL_DETAIL_TAB_ROUTE, redirect: alwaysTo('billDetailOverview') },
  billDetailOverview: { ...BILL_DETAIL_TAB_ROUTE, ...RECORD_LANDING_DENIAL, segment: billTabSegment('overview') },
  billDetailConversations: { ...BILL_DETAIL_TAB_ROUTE, segment: billTabSegment('conversations') },
  billDetailRelated: { ...BILL_DETAIL_TAB_ROUTE, segment: billTabSegment('related') },
  productCatalogues: {
    pattern: '/products-and-services',
    availability: any([all([orgFlag('PUNCHOUT_CATALOGUES'), ldFlag('SHOW_PUNCHOUT_CATALOGUES')]), orgFlag('HOSTED_CATALOGUES')]),
    authorization: { kind: 'open' },
  },
  sourcingDashboard: {
    pattern: '/sourcing/[[...mode]]',
    availability: ldFlag('RELEASE_S_AND_P_SAVINGS_DASHBOARD'),
    authorization: { kind: 'open' },
  },
  risksList: {
    pattern: '/risks/[[...mode]]',
    availability: orgFlag('TPRM'),
    authorization: { kind: 'open' },
  },
  riskAlertsList: {
    pattern: '/risk-alerts/[[...mode]]',
    availability: all([orgFlag('TPRM'), orgFlag('TPRM_CONTINUOUS_MONITORING')]),
    authorization: { kind: 'open' },
  },
  subprocessorsList: {
    pattern: '/subprocessors/[[...mode]]',
    availability: orgFlag('SUPPLIER_SUBPROCESSORS'),
    authorization: tprmLevelsSwitch({
      flagOff: { kind: 'role', role: adminsOnly },
      flagOn: { kind: 'fgaOrRole', fga: viewsAllSubprocessors, role: adminsOnly },
    }),
  },
  // Insights opens on Custom Dashboards; linking to the bare `/insights` costs a client redirect.
  insights: { ...REPORTING_ROUTE, pattern: '/insights/[[...path]]', href: '/insights/custom-dashboards' },
  reporting: { ...REPORTING_ROUTE, pattern: '/reporting/[[...path]]' },
  reportingDataDestinations: {
    pattern: '/reporting/data-destinations',
    availability: ldFlag('RELEASE_REPORTING_CLOUD_DELIVERY_UI_V2'),
    // Every action on the page mutates, and the page offers them to whoever reaches it.
    authorization: { kind: 'fgaOrRole', fga: managesInsights, role: superAdmin },
    redirect: onDenial(DEFAULT_DENIED_REDIRECT),
  },
  workflowsList: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows' },
  workflowAuditLog: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows/edit/[id]/audit-log', params: ['id'] },
  workflowDraftEdit: {
    ...WORKFLOW_EDITOR_ROUTE,
    pattern: '/workflows/edit/[id]/drafts/[draftId]',
    params: ['id', 'draftId'],
  },
  workflowEdit: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/workflows/edit/[id]', params: ['id'] },
  settingsPermissions: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions' },
  settingsPermissionsConfigAccess: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/config-access' },
  settingsPermissionsRoles: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/roles' },
  settingsPermissionsPermissionSets: { ...PERMISSIONS_ROUTE, pattern: '/settings/permissions/permission-sets' },
  settingsPermissionsPermissionSetsEntity: {
    ...PERMISSION_SET_PAGE_ROUTE,
    redirect: alwaysTo('settingsPermissionsPermissionSets'),
  },
  settingsPermissionsPermissionSetsRequest: {
    ...PERMISSION_SET_PAGE_ROUTE,
    segment: permissionSetPageSegment('request'),
  },
  settingsPermissionsPermissionSetsSupplier: {
    ...PERMISSION_SET_PAGE_ROUTE,
    segment: permissionSetPageSegment('supplier'),
  },
  settingsPermissionsPermissionSetsAgreement: {
    ...PERMISSION_SET_PAGE_ROUTE,
    segment: permissionSetPageSegment('agreement'),
  },
  settingsNotifications: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('notifications'),
    availability: always(),
    authorization: { kind: 'open' },
  },
  settingsAgentLibrary: {
    pattern: '/settings/agent-library',
    availability: ldFlag('RELEASE_AGENT_MARKETPLACE_V1'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(DEFAULT_DENIED_REDIRECT),
  },
  settingsEnvironmentSync: { ...ENVIRONMENT_SYNC_ROUTE, pattern: '/settings/environment-sync' },
  settingsEnvironmentSyncJob: {
    ...ENVIRONMENT_SYNC_ROUTE,
    pattern: '/settings/environment-sync/[workflowId]/[jobId]',
    params: ['workflowId', 'jobId'],
  },
  settingsAdmin: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('admin'),
    availability: always(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsAgreementsMigration: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsAgreementsMigrationConfigureMappings: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-configure-mappings'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsAgreementsMigrationMigrateEngagements: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-migrate-engagements'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsAgreementsMigrationMigrateRenewals: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('agreements-migration-migrate-renewals'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsAi: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('ai'),
    availability: always(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsAuctions: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('auctions'),
    availability: orgFlag('AUCTIONS'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsAiBulkExtraction: {
    ...AI_BULK_EXTRACTION_ROUTE,
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('ai-bulk-extraction'),
  },
  settingsAiBulkExtractionJob: {
    ...AI_BULK_EXTRACTION_ROUTE,
    pattern: '/settings/ai-bulk-extraction/[jobId]',
    params: ['jobId'],
  },
  settingsAiBulkExtractionJobReview: {
    ...AI_BULK_EXTRACTION_ROUTE,
    pattern: '/settings/ai-bulk-extraction/[jobId]/review',
    params: ['jobId'],
  },
  settingsApi: { ...PUBLIC_API_ROUTE, pattern: SETTINGS_PATTERN, segment: modeSegment('api') },
  settingsApiOAuthApp: { ...PUBLIC_API_ROUTE, pattern: '/settings/api/oauth/[integrationId]', params: ['integrationId'] },
  settingsApiOAuthEntities: {
    ...PUBLIC_API_ROUTE,
    pattern: '/settings/api/oauth/[integrationId]/entities',
    params: ['integrationId'],
  },
  settingsApiOAuthEntityFields: {
    ...PUBLIC_API_ROUTE,
    pattern: '/settings/api/oauth/[integrationId]/fields/[entityType]',
    params: ['integrationId', 'entityType'],
  },
  settingsAuditLog: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('audit-log'),
    availability: always(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  // Reached only from a card on the Import page. The import it runs takes a system admin, so the page
  // does too; the Import page hides the card on the same rule.
  settingsBulkDocumentsUpload: {
    pattern: '/settings/bulk-documents-upload',
    availability: always(),
    authorization: { kind: 'fgaOrRole', fga: isSystemAdmin, role: superAdmin },
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsExternalArchive: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('external-archive'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsAutoFlaggedCategories: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('auto-flagged-categories'),
    availability: orgFlag('TPRM'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('catalogues'),
    availability: any([orgFlag('PRODUCTS_AND_SERVICES_CATALOGUE'), orgFlag('PUNCHOUT_CATALOGUES'), orgFlag('HOSTED_CATALOGUES')]),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsDynamicAssignees: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('dynamic-assignees'),
    availability: always(),
    authorization: configGated('dynamic_assignees'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsCoreData: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('core-data'),
    availability: always(),
    authorization: configGated('core_data'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsDefaultNotifications: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('default-notifications'),
    availability: always(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsDocumentStorage: { ...DOCUMENT_STORAGE_ROUTE, pattern: '/settings/document-storage' },
  settingsDocumentStorageType: {
    ...DOCUMENT_STORAGE_ROUTE,
    pattern: '/settings/document-storage/types/[id]',
    params: ['id'],
  },
  settingsInternalFeatureFlags: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('__feature-flags'),
    availability: omneanOnly(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsForms: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('forms'),
    availability: always(),
    authorization: configGated('workflows_and_forms', superAdminOrP2pAdmin),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsImport: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('import'),
    availability: always(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsInherentRiskAssessment: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('inherent-risk-assessment'),
    availability: orgFlag('TPRM'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsIntegrations: { ...INTEGRATIONS_ROUTE, pattern: SETTINGS_PATTERN, segment: modeSegment('integrations') },
  settingsIntegrationEntities: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/entities',
    params: ['integrationId'],
  },
  settingsIntegrationEntityFields: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/fields/[entityType]',
    params: ['integrationId', 'entityType'],
  },
  settingsIntegrationFilters: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/filters',
    params: ['integrationId'],
  },
  settingsIntegrationPermissions: {
    ...INTEGRATIONS_ROUTE,
    pattern: '/settings/integrations/[integrationId]/permissions',
    params: ['integrationId'],
  },
  settingsIntegrationProcedures: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/procedures',
    params: ['integrationId'],
  },
  settingsIntegrationProcedureTemplate: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/procedures/[templateId]',
    params: ['integrationId', 'templateId'],
  },
  settingsIntegrationVariables: {
    ...INTEGRATION_BUILDER_ROUTE,
    pattern: '/settings/integrations/[integrationId]/variables',
    params: ['integrationId'],
  },
  settingsLocalisation: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('localisation'),
    availability: orgFlag('CONFIG_TRANSLATIONS'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsPolicies: {
    pattern: '/settings/policies',
    availability: always(),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  // A policy is shared by link, so its own page stays reachable by anyone the documents service will
  // answer for; the store that lists every policy does not.
  settingsPolicy: { pattern: '/settings/policies/[id]', params: ['id'], availability: always(), authorization: { kind: 'open' } },
  settingsPreferences: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('preferences'),
    availability: always(),
    authorization: { kind: 'open' },
  },
  settingsProductCatalogue: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('product-catalogue'),
    availability: orgFlag('PRODUCTS_AND_SERVICES_CATALOGUE'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsPunchoutCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('punchout-catalogues'),
    availability: all([omneanOnly(), orgFlag('PUNCHOUT_CATALOGUES'), ldFlag('SHOW_PUNCHOUT_CATALOGUES')]),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsHostedCatalogues: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('hosted-catalogues'),
    availability: orgFlag('HOSTED_CATALOGUES'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsCategories: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('categories'),
    availability: any([orgFlag('PRODUCTS_AND_SERVICES_CATALOGUE'), orgFlag('SUPPLIER_CATEGORISATION')]),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsRisks: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risks'),
    availability: orgFlag('TPRM'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsRiskManagement: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risk-management'),
    availability: orgFlag('TPRM'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsRiskAlerts: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('risk-alerts'),
    availability: all([orgFlag('TPRM'), orgFlag('TPRM_CONTINUOUS_MONITORING')]),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsSubprocessorSettings: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('subprocessor-settings'),
    availability: all([orgFlag('TPRM'), orgFlag('SUPPLIER_SUBPROCESSORS')]),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsArtefactCatalogue: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefact-catalogue'),
    availability: ARTEFACTS_AGENT_AVAILABILITY,
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsArtefactGroups: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefact-groups'),
    availability: ARTEFACTS_AGENT_AVAILABILITY,
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsArtefactsLibrary: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('artefacts-library'),
    availability: ARTEFACTS_AGENT_AVAILABILITY,
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsGoodsReceipting: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('goods-receipting'),
    availability: orgFlag('GOODS_RECEIPTING'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsPurchaseOrders: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('purchase-orders'),
    availability: orgFlag('PURCHASE_ORDER_ISSUANCE'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsPurchaseOrderTemplates: {
    pattern: '/settings/purchase-orders/templates',
    availability: orgFlag('PURCHASE_ORDER_ISSUANCE'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsInvoiceProcessing: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('invoice-processing'),
    availability: orgFlag('INVOICE_PROCESSING'),
    authorization: configGated('invoice_processing'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsSecurity: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('security'),
    availability: always(),
    authorization: configGated('it'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsSourcing: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('sourcing'),
    availability: orgFlag('RFX'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsSourcingCapabilities: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('sourcing-capabilities'),
    availability: orgFlag('RFX'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsSourcingScorecardTemplates: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('sourcing-scorecard-templates'),
    availability: orgFlag('RFX'),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsSourcingMemory: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('sourcing-memory'),
    availability: all([omneanOnly(), orgFlag('RFX')]),
    authorization: configGated('organisation_settings'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsUserProperties: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('user-properties'),
    availability: always(),
    authorization: configGated('it'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsUsersAndGroups: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('users-and-groups'),
    availability: always(),
    authorization: configGated('users_and_groups'),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsWorkflows: {
    pattern: SETTINGS_PATTERN,
    segment: modeSegment('workflows'),
    availability: always(),
    authorization: configGated('workflows_and_forms', superAdminOrP2pAdmin),
    redirect: onDenial(SETTINGS_FALLBACK_ROUTE),
  },
  settingsWorkflowDraftEdit: {
    ...WORKFLOW_EDITOR_ROUTE,
    pattern: '/settings/workflows/edit/[id]/drafts/[draftId]',
    params: ['id', 'draftId'],
  },
  settingsWorkflowEdit: { ...WORKFLOW_EDITOR_ROUTE, pattern: '/settings/workflows/edit/[id]', params: ['id'] },
} as const satisfies Readonly<Record<RouteKey, RouteDefinition>>

// `Object.entries` widens the key to `string`; `ROUTE_REGISTRY` is `Record<RouteKey, …>`, so every key is a `RouteKey`.
export const ROUTE_REGISTRY_ENTRIES = Object.entries(ROUTE_REGISTRY) as readonly [RouteKey, RouteDefinition][]

export type RecordRouteKey = {
  [Key in RouteKey]: (typeof ROUTE_REGISTRY)[Key] extends { record: RecordGate } ? Key : never
}[RouteKey]

/**
 * The page a link to a record opens on. Not `RECORD_LANDING_KEYS`, which is where a denied sub-route falls
 * back to and so may not show the record at all. A workflow has no page of its own, so it has no entry.
 */
export const RECORD_PAGE_KEYS = {
  request: 'requestDetailOverview',
  supplier: 'supplierDetailOverview',
  agreement: 'agreementDetailOverview',
  bill: 'billDetail',
} as const satisfies Readonly<Partial<Record<PermissionSetEntity, RecordRouteKey>>>

export type RecordPageEntity = keyof typeof RECORD_PAGE_KEYS
